import { useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import InputBase from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import SearchIcon from '@mui/icons-material/Search';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import PictureAsPdfOutlinedIcon from '@mui/icons-material/PictureAsPdfOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import NavigateBeforeIcon from '@mui/icons-material/NavigateBefore';
import NavigateNextIcon from '@mui/icons-material/NavigateNext';
import { SelectChangeEvent } from '@mui/material/Select';
import { abyssColors } from '@/theme/colors';
import { useGenerateReportPdfMutation } from '@/__generated__/graphql';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useGetReports, ReportTypeTab } from './useGetReports';
import { ReportDetailDrawer } from './ReportDetailDrawer';
import { useInsightsPageParams, useRouter } from '@/hooks';

const TABS: { key: ReportTypeTab; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'COMPONENT', label: 'Components' },
  { key: 'POINT_OF_INTEREST', label: 'Point of interests' },
  { key: 'PAINT_BLOCK', label: 'Paint block' },
];

type ComponentReportsProps = {
  componentId?: string;
  pointOfInterestId?: string;
  initialTab?: ReportTypeTab;
  hideTabs?: boolean;
};

export const ComponentReports = ({
  componentId,
  pointOfInterestId,
  initialTab,
  hideTabs = false,
}: ComponentReportsProps = {}) => {
  const router = useRouter();
  const { removeParams } = useInsightsPageParams();
  const {
    reports,
    totalReports,
    loading,
    searchTerm,
    setSearchTerm,
    activeTab,
    setActiveTab,
    page,
    setPage,
    rowsPerPage,
    setRowsPerPage,
    totalPages,
  } = useGetReports({ componentId, pointOfInterestId, initialTab, reverse: true });

  const [selectedReportId, setSelectedReportId] = useState<string | null>(null);
  const [downloadingReportId, setDownloadingReportId] = useState<string | null>(null);
  const [generateReportPdf] = useGenerateReportPdfMutation();
  const { showSnackBar } = useSnackBarMessage({ isReportDownload: true });

  const handleViewReport = (reportId: string) => {
    setSelectedReportId(reportId);
  };

  const handleCloseDrawer = () => {
    setSelectedReportId(null);
    removeParams(['reportId']);
  };

  const reportIdFromUrl = (() => {
    const raw = router.query?.reportId;
    if (typeof raw === 'string') return raw;
    if (Array.isArray(raw)) return raw[0];
    return undefined;
  })();

  useEffect(() => {
    // In kendo inspection planner we use a dedicated global drawer instead.
    if (router.pathname.includes('insights-kendo')) return;
    if (reportIdFromUrl) {
      setSelectedReportId(reportIdFromUrl);
    }
  }, [reportIdFromUrl, router.pathname]);

  const handleDownloadPdf = async (reportId: string) => {
    if (downloadingReportId) return;
    try {
      setDownloadingReportId(reportId);
      showSnackBar('Preparing your report. Download will start once ready.', 'info', {
        persistent: true,
      });
      const { data } = await generateReportPdf({
        variables: { reportIds: [reportId] },
      });
      if (data?.generateReportPdf?.downloadUrl?.length) {
        const downloadUrl = data.generateReportPdf.downloadUrl[0];
        if (downloadUrl) {
          showSnackBar('Your report is ready. Download is starting...', 'success');
          setTimeout(() => {
            window.open(downloadUrl, '_blank');
          }, 1_000);
        }
      }
    } catch {
      showSnackBar('Download failed. Please try again.', 'error');
    } finally {
      setDownloadingReportId(null);
    }
  };

  const handleRowsPerPageChange = (event: SelectChangeEvent<number>) => {
    setRowsPerPage(Number(event.target.value));
  };

  const startIndex = totalReports > 0 ? (page - 1) * rowsPerPage + 1 : 0;
  const endIndex = Math.min(page * rowsPerPage, totalReports);

  return (
    <Box>
      <Box
        sx={{
          border: `1px solid ${abyssColors.primary[200]}`,
          borderRadius: '4px',
          display: 'flex',
          alignItems: 'center',
          px: 1,
          py: 0.5,
          mb: 1.5,
          backgroundColor: 'white',
        }}
      >
        <SearchIcon sx={{ color: abyssColors.primary[400], fontSize: 20, mr: 1 }} />
        <InputBase
          placeholder="Search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
          sx={{
            flex: 1,
            fontSize: '14px',
            fontFamily: 'Roboto, sans-serif',
            color: abyssColors.primary[500],
            '& input::placeholder': {
              color: abyssColors.primary[400],
              opacity: 0.75,
            },
          }}
        />
      </Box>

      {!hideTabs && (
        <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
          {TABS.map((tab) => (
            <Box
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              sx={{
                px: 2,
                py: 0.5,
                borderRadius: '4px',
                cursor: 'pointer',
                backgroundColor: activeTab === tab.key ? abyssColors.primary[100] : 'transparent',
                '&:hover': {
                  backgroundColor: abyssColors.primary[100],
                },
              }}
            >
              <Typography
                sx={{
                  fontSize: '12px',
                  fontWeight: 600,
                  fontFamily: 'Roboto, sans-serif',
                  color:
                    activeTab === tab.key ? abyssColors.primary[500] : abyssColors.primary[300],
                  whiteSpace: 'nowrap',
                }}
              >
                {tab.label}
              </Typography>
            </Box>
          ))}
        </Stack>
      )}

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={24} sx={{ color: abyssColors.primary[500] }} />
        </Box>
      )}

      {!loading && reports.length === 0 && (
        <Box sx={{ py: 4, textAlign: 'center' }}>
          <Typography sx={{ fontSize: '14px', color: abyssColors.primary[400] }}>
            No reports found
          </Typography>
        </Box>
      )}

      {!loading && reports.length > 0 && (
        <Stack spacing={0.75}>
          {reports.map((report) => {
            if (!report?.id) return null;
            const reportId = report.id;
            return (
              <Box
                key={reportId}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  backgroundColor: abyssColors.primary[100],
                  borderRadius: '4px',
                  height: '40px',
                  px: 1.5,
                  opacity: 0.8,
                  transition: 'opacity 0.15s ease',
                  '&:hover': {
                    opacity: 1,
                  },
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  <DescriptionOutlinedIcon
                    sx={{
                      fontSize: 16,
                      color: abyssColors.primary[500],
                      flexShrink: 0,
                    }}
                  />
                  <Typography
                    sx={{
                      fontSize: '14px',
                      fontFamily: 'Roboto, sans-serif',
                      color: abyssColors.primary[500],
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    }}
                  >
                    {report.name}
                  </Typography>
                </Stack>

                <Stack direction="row" alignItems="center" spacing={0.5} sx={{ flexShrink: 0 }}>
                  <IconButton
                    size="small"
                    onClick={() => handleViewReport(reportId)}
                    sx={{
                      padding: '4px',
                      '&:hover': {
                        backgroundColor: abyssColors.primary[200],
                      },
                    }}
                  >
                    <VisibilityOutlinedIcon
                      sx={{ fontSize: 16, color: abyssColors.primary[500] }}
                    />
                  </IconButton>

                  {downloadingReportId === reportId ? (
                    <CircularProgress
                      size={16}
                      sx={{
                        color: abyssColors.error[500],
                        mx: '4px',
                      }}
                    />
                  ) : (
                    <IconButton
                      size="small"
                      onClick={() => handleDownloadPdf(reportId)}
                      sx={{
                        padding: '4px',
                        '&:hover': {
                          backgroundColor: abyssColors.error[50],
                        },
                      }}
                    >
                      <PictureAsPdfOutlinedIcon
                        sx={{ fontSize: 16, color: abyssColors.error[500] }}
                      />
                    </IconButton>
                  )}
                </Stack>
              </Box>
            );
          })}
        </Stack>
      )}

      {!loading && totalReports > 0 && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="flex-end"
          spacing={1.5}
          sx={{ mt: 2 }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              color: abyssColors.primary[500],
              fontFamily: 'Roboto, sans-serif',
            }}
          >
            Rows per page
          </Typography>
          <Select
            value={rowsPerPage}
            onChange={handleRowsPerPageChange}
            size="small"
            sx={{
              fontSize: '12px',
              height: '26px',
              minWidth: '50px',
              backgroundColor: abyssColors.primary[50],
              border: `1px solid ${abyssColors.primary[300]}`,
              borderRadius: '4px',
              color: abyssColors.primary[500],
              '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
              },
              '& .MuiSelect-select': {
                py: 0,
                px: 1,
              },
            }}
          >
            {[5, 10, 25].map((value) => (
              <MenuItem key={value} value={value} sx={{ fontSize: '12px' }}>
                {String(value).padStart(2, '0')}
              </MenuItem>
            ))}
          </Select>

          <Stack direction="row" alignItems="center" spacing={0.75}>
            <IconButton
              size="small"
              disabled={page <= 1}
              onClick={() => setPage(page - 1)}
              sx={{ padding: '2px' }}
            >
              <NavigateBeforeIcon sx={{ fontSize: 16, color: abyssColors.primary[500] }} />
            </IconButton>

            <Typography
              sx={{
                fontSize: '14px',
                color: abyssColors.primary[500],
                fontFamily: 'Roboto, sans-serif',
                whiteSpace: 'nowrap',
                mx: 1,
              }}
            >
              {`${startIndex}–${endIndex} of ${totalReports}`}
            </Typography>

            <IconButton
              size="small"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              sx={{ padding: '2px' }}
            >
              <NavigateNextIcon sx={{ fontSize: 16, color: abyssColors.primary[500] }} />
            </IconButton>
          </Stack>
        </Stack>
      )}

      <ReportDetailDrawer
        reportId={selectedReportId}
        open={selectedReportId !== null}
        onClose={handleCloseDrawer}
      />
    </Box>
  );
};
